import { EmailSchema, PasswordSchema } from "@adpilot/shared";
import { IconKey } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCountdown } from "@/hooks/use-countdown";
import { getApiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";

interface ForgotPasswordFormProps {
  prefillEmail?: string;
  onSendCode: (email: string) => Promise<void>;
  onReset: (email: string, code: string, newPassword: string) => Promise<void>;
  onBack: () => void;
  className?: string;
}

/**
 * 忘记密码 — email code + new password (ADP-167). Also the "set password"
 * path for legacy/OAuth users without one.
 */
export function ForgotPasswordForm({
  prefillEmail,
  onSendCode,
  onReset,
  onBack,
  className,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation("auth");
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const [focusRequest, setFocusRequest] = useState<{
    field: "email" | "code" | "password" | "confirm";
  } | null>(null);
  const [errorField, setErrorField] = useState<"email" | "code" | "confirm" | null>(null);
  useEffect(() => {
    if (!focusRequest) return;
    const refs = { email: emailRef, code: codeRef, password: passwordRef, confirm: confirmRef };
    refs[focusRequest.field].current?.focus();
  }, [focusRequest]);
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValidated, setPasswordValidated] = useState(false);
  const passwordInvalid = passwordValidated && !PasswordSchema.safeParse(newPassword).success;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { remaining: countdown, isExpired, reset: resetCountdown } = useCountdown(0);

  async function handleSendCode() {
    const submittedEmail = email.trim().toLowerCase();
    setErrorField(null);
    if (!EmailSchema.safeParse(submittedEmail).success) {
      setErrorField("email");
      setFocusRequest({ field: "email" });
      setError(t("login.invalidEmail"));
      return;
    }
    setError(null);
    setSending(true);
    setCodeSent(false);
    try {
      await onSendCode(submittedEmail);
      setCodeSent(true);
      resetCountdown(60);
    } catch (err) {
      setError(await getApiErrorMessage(err));
    } finally {
      setSending(false);
    }
  }

  async function handleSubmit() {
    const submittedEmail = email.trim().toLowerCase();
    setErrorField(null);
    if (!EmailSchema.safeParse(submittedEmail).success) {
      setErrorField("email");
      setFocusRequest({ field: "email" });
      setError(t("login.invalidEmail"));
      return;
    }
    if (code.length !== 6) {
      setErrorField("code");
      setFocusRequest({ field: "code" });
      setError(t("login.forgot.codeRequired"));
      return;
    }
    setPasswordValidated(true);
    if (!PasswordSchema.safeParse(newPassword).success) {
      setError(null);
      setFocusRequest({ field: "password" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorField("confirm");
      setFocusRequest({ field: "confirm" });
      setError(t("login.forgot.passwordMismatch"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onReset(submittedEmail, code, newPassword);
    } catch (err) {
      setError(await getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      noValidate
      className={cn("flex min-h-[320px] flex-col gap-7", className)}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <div>
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <IconKey className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("login.forgot.title")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("login.forgot.description")}</p>
      </div>

      <div className="flex flex-col gap-3">
        <Input
          id="forgot-email"
          name="username"
          ref={emailRef}
          aria-invalid={errorField === "email"}
          aria-describedby={errorField === "email" ? "forgot-error" : undefined}
          type="email"
          aria-label={t("login.forgot.emailLabel")}
          placeholder={t("login.emailPlaceholder")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          size="lg"
        />
        <div className="flex gap-2">
          <Input
            id="forgot-code"
            name="verification-code"
            ref={codeRef}
            aria-invalid={errorField === "code"}
            aria-describedby={errorField === "code" ? "forgot-error" : undefined}
            required
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            aria-label={t("login.forgot.codePlaceholder")}
            placeholder={t("login.forgot.codePlaceholder")}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            className="flex-1"
            size="lg"
          />
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={sending || (codeSent && !isExpired)}
            onClick={() => void handleSendCode()}
          >
            {codeSent && !isExpired
              ? t("otp.resendIn", { seconds: countdown })
              : t("login.forgot.sendCode")}
          </Button>
        </div>
        <p role="status" className="text-xs text-muted-foreground empty:hidden">
          {codeSent ? t("login.forgot.codeSent") : ""}
        </p>
        <Input
          id="forgot-new-password"
          name="new-password"
          ref={passwordRef}
          aria-invalid={passwordInvalid}
          aria-describedby="forgot-password-policy"
          type="password"
          aria-label={t("login.forgot.newPasswordPlaceholder")}
          placeholder={t("login.forgot.newPasswordPlaceholder")}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          required
          autoComplete="new-password"
          size="lg"
        />
        <Input
          id="forgot-confirm-password"
          name="confirm-password"
          ref={confirmRef}
          aria-invalid={errorField === "confirm"}
          aria-describedby={errorField === "confirm" ? "forgot-error" : undefined}
          type="password"
          aria-label={t("login.forgot.confirmPasswordPlaceholder")}
          placeholder={t("login.forgot.confirmPasswordPlaceholder")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          required
          autoComplete="new-password"
          size="lg"
        />
        <p
          id="forgot-password-policy"
          className={cn(
            "text-xs",
            passwordInvalid ? "text-destructive-foreground" : "text-muted-foreground",
          )}
          aria-live="polite"
        >
          {passwordInvalid ? `${t("login.forgot.passwordInvalid")} ` : ""}
          {t("login.forgot.passwordPolicy")}
        </p>
        <p
          id="forgot-error"
          role="alert"
          className="text-sm text-destructive-foreground empty:hidden"
        >
          {error}
        </p>
        <Button type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? t("login.forgot.submitting") : t("login.forgot.submit")}
        </Button>
        <button
          type="button"
          className="self-center text-sm text-muted-foreground hover:text-foreground hover:underline"
          onClick={onBack}
        >
          {t("login.forgot.backToLogin")}
        </button>
      </div>
    </form>
  );
}
